import React, { useState, useRef, useEffect } from "react";
import {
  Box, Button, Spinner, TextFilter, Input, Modal,
  SpaceBetween, Textarea, FormField, SegmentedControl, Icon,
} from "@cloudscape-design/components";
import { useChatSession } from "../../context/ChatSessionContext";

// Group sessions into date buckets
function groupSessions(sessions) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yest  = new Date(today); yest.setDate(yest.getDate() - 1);
  const week  = new Date(today); week.setDate(week.getDate() - 7);
  const month = new Date(today); month.setDate(month.getDate() - 30);

  const buckets = [
    { label: "Today",        items: [] },
    { label: "Yesterday",    items: [] },
    { label: "Last 7 days",  items: [] },
    { label: "Last 30 days", items: [] },
    { label: "Older",        items: [] },
  ];

  for (const s of sessions) {
    const d = new Date(s.updated_at);
    if      (d >= today) buckets[0].items.push(s);
    else if (d >= yest)  buckets[1].items.push(s);
    else if (d >= week)  buckets[2].items.push(s);
    else if (d >= month) buckets[3].items.push(s);
    else                 buckets[4].items.push(s);
  }

  return buckets.filter(b => b.items.length > 0);
}

export default function ChatHistorySidebar() {
  const {
    sessions, currentSessionId, sessionsLoaded,
    openSession, startNewChat, deleteSession, renameSession, pinSession,
    savedPrompts, loadSavedPrompts, deleteSavedPrompt,
    pendingPromptText, setPendingPromptText, setQueryMode,
  } = useChatSession();

  // Tab state
  const [activeTab, setActiveTab]               = useState("chats");

  // Chats tab state
  const [hoveredSession, setHoveredSession]     = useState(null);
  const [search, setSearch]                     = useState("");
  const [editingId, setEditingId]               = useState(null);
  const [editTitle, setEditTitle]               = useState("");
  const [deleteTargetId, setDeleteTargetId]     = useState(null);

  // Saved prompts tab state
  const [promptSearch, setPromptSearch]         = useState("");
  const [hoveredPromptId, setHoveredPromptId]   = useState(null);
  const [deletePromptId, setDeletePromptId]     = useState(null);
  const [usePromptModal, setUsePromptModal]     = useState(null);
  const [editedPromptText, setEditedPromptText] = useState("");

  const inputRef = useRef(null);

  useEffect(() => { loadSavedPrompts(); }, [loadSavedPrompts]);

  // Split filtered sessions into pinned + date-grouped
  const filtered = search.trim()
    ? sessions.filter(s => (s.title || "").toLowerCase().includes(search.trim().toLowerCase()))
    : sessions;
  const pinnedSessions   = filtered.filter(s => s.pinned);
  const unpinnedSessions = filtered.filter(s => !s.pinned);
  const groups           = groupSessions(unpinnedSessions);
  const hasAnySessions   = pinnedSessions.length > 0 || groups.length > 0;

  // Filtered prompts for saved prompts tab
  const filteredPrompts = promptSearch.trim()
    ? savedPrompts.filter(p => (p.title || "").toLowerCase().includes(promptSearch.trim().toLowerCase()))
    : savedPrompts;

  const startEdit = (e, s) => {
    e.stopPropagation();
    setEditingId(s.session_id);
    setEditTitle(s.title || "");
  };

  const commitEdit = async (sessionId) => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== sessions.find(s => s.session_id === sessionId)?.title) {
      await renameSession(sessionId, trimmed);
    }
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  // Shared session row renderer
  const renderSession = (s) => {
    const isActive  = s.session_id === currentSessionId;
    const isHovered = s.session_id === hoveredSession;
    const isEditing = s.session_id === editingId;
    const isPinned  = !!s.pinned;

    return (
      <div
        key={s.session_id}
        onClick={() => { if (!isEditing) openSession(s); }}
        onMouseEnter={() => setHoveredSession(s.session_id)}
        onMouseLeave={() => setHoveredSession(null)}
        style={{
          padding:      "7px 12px",
          cursor:       isEditing ? "default" : "pointer",
          borderBottom: "1px solid #f0f0f0",
          position:     "relative",
          borderLeft:   isActive ? "3px solid #0972d3" : "3px solid transparent",
          background:   isActive ? "#f0f4ff" : isHovered ? "#f8f8f8" : "transparent",
          transition:   "background 0.1s",
        }}
      >
        {isEditing ? (
          <Input
            ref={inputRef}
            autoFocus
            value={editTitle}
            onChange={({ detail }) => setEditTitle(detail.value)}
            onKeyDown={({ detail }) => {
              if (detail.key === "Enter")  commitEdit(s.session_id);
              if (detail.key === "Escape") cancelEdit();
            }}
            onBlur={() => commitEdit(s.session_id)}
          />
        ) : (
          <Box
            fontSize="body-m"
            fontWeight={isActive ? "bold" : "normal"}
            color={isActive ? "text-status-info" : "text-label"}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {isPinned && (
                <span style={{ flexShrink: 0, opacity: 0.45, display: "flex", alignItems: "center" }}>
                  <Icon name="star-filled" size="small" />
                </span>
              )}
              <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: (isHovered || isActive) ? 68 : 0 }}>
                {s.title || "Untitled"}
              </span>
            </div>
          </Box>
        )}

        {!isEditing && (isHovered || isActive) && (
          <div style={{ position: "absolute", top: 5, right: 4, display: "flex", gap: 0 }}>
            <Button
              variant="icon"
              iconName="edit"
              ariaLabel="Rename chat"
              onClick={(e) => startEdit(e, s)}
            />
            <Button
              variant="icon"
              iconName={isPinned ? "star-filled" : "star"}
              ariaLabel={isPinned ? "Unpin chat" : "Pin chat"}
              onClick={(e) => { e.stopPropagation(); pinSession(s.session_id, !isPinned); }}
            />
            <Button
              variant="icon"
              iconName="remove"
              ariaLabel="Delete chat"
              onClick={(e) => { e.stopPropagation(); setDeleteTargetId(s.session_id); }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Tab switcher + New Chat icon — single compact row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid #e3e8ef", flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <SegmentedControl
            selectedId={activeTab}
            onChange={({ detail }) => setActiveTab(detail.selectedId)}
            options={[
              { id: "chats",   text: "Chats", title: "Recent Chats" },
              { id: "prompts", text: "Prompts", title: "Saved Prompts" },
            ]}
          />
        </div>
        <div
          onClick={startNewChat}
          title="New chat"
          style={{
            width: 30, height: 30, borderRadius: "50%",
            background: "#0972d3",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0,
          }}
        >
          <Icon name="add-plus" variant="inverted" />
        </div>
      </div>

      {/* Search — switches with active tab */}
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #e3e8ef", flexShrink: 0 }}>
        {activeTab === "chats" ? (
          <TextFilter
            filteringText={search}
            filteringPlaceholder="Recent chats..."
            onChange={({ detail }) => setSearch(detail.filteringText)}
          />
        ) : (
          <TextFilter
            filteringText={promptSearch}
            filteringPlaceholder="Saved prompts..."
            onChange={({ detail }) => setPromptSearch(detail.filteringText)}
          />
        )}
      </div>

      {/* Scrollable content area */}
      <div style={{ overflowY: "auto", flex: 1 }}>

        {/* ── Chats tab ── */}
        {activeTab === "chats" && (
          !sessionsLoaded ? (
            <div style={{ padding: 16, textAlign: "center" }}><Spinner size="normal" /></div>
          ) : !hasAnySessions ? (
            <div style={{ padding: "16px 12px" }}>
              <Box color="text-body-secondary" fontSize="body-s">
                {search.trim() ? "No matching chats" : "No previous chats"}
              </Box>
            </div>
          ) : (
            <>
              {/* Pinned sessions — float to top, no label */}
              {pinnedSessions.map(s => renderSession(s))}

              {/* Date-grouped unpinned sessions */}
              {groups.map(group => (
                <div key={group.label}>
                  <Box padding={{ horizontal: "s", vertical: "xxxs" }} color="text-body-secondary" fontSize="body-s">
                    {group.label}
                  </Box>
                  {group.items.map(s => renderSession(s))}
                </div>
              ))}
            </>
          )
        )}

        {/* ── Saved Prompts tab ── */}
        {activeTab === "prompts" && (
          filteredPrompts.length === 0 ? (
            <div style={{ padding: "16px 12px" }}>
              <Box color="text-body-secondary" fontSize="body-s">
                {promptSearch.trim() ? "No matching prompts." : "No saved prompts yet. Save a message from a chat to reuse it."}
              </Box>
            </div>
          ) : (
            filteredPrompts.map(p => (
              <div
                key={p.prompt_id}
                onClick={() => { setUsePromptModal(p); setEditedPromptText(p.content); }}
                onMouseEnter={() => setHoveredPromptId(p.prompt_id)}
                onMouseLeave={() => setHoveredPromptId(null)}
                style={{
                  padding:      "7px 12px",
                  cursor:       "pointer",
                  borderBottom: "1px solid #f0f0f0",
                  position:     "relative",
                  background:   hoveredPromptId === p.prompt_id ? "#f8f8f8" : "transparent",
                  transition:   "background 0.1s",
                }}
              >
                <Box fontSize="body-m" color="text-label">
                  <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: hoveredPromptId === p.prompt_id ? 36 : 0 }}>
                    {p.title}
                  </span>
                </Box>
                {hoveredPromptId === p.prompt_id && (
                  <div style={{ position: "absolute", top: 5, right: 4 }}>
                    <Button
                      variant="icon"
                      iconName="remove"
                      ariaLabel="Delete saved prompt"
                      onClick={(e) => { e.stopPropagation(); setDeletePromptId(p.prompt_id); }}
                    />
                  </div>
                )}
              </div>
            ))
          )
        )}
      </div>

      {/* Delete chat confirmation modal */}
      <Modal
        visible={deleteTargetId !== null}
        onDismiss={() => setDeleteTargetId(null)}
        header="Delete chat"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteTargetId(null)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => { deleteSession(deleteTargetId); setDeleteTargetId(null); }}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>Are you sure you want to delete this chat? This action cannot be undone.</Box>
      </Modal>

      {/* Use saved prompt modal */}
      <Modal
        visible={usePromptModal !== null}
        onDismiss={() => setUsePromptModal(null)}
        header={usePromptModal?.title || "Use Prompt"}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setUsePromptModal(null)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => {
                  startNewChat();
                  setQueryMode(usePromptModal.query_mode || "schema");
                  setPendingPromptText(editedPromptText);
                  setUsePromptModal(null);
                }}
              >
                Use Prompt
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box color="text-body-secondary">
            Edit the prompt below — replace any placeholders like [DC_NAME] or [DATE_RANGE] — then click Use Prompt.
          </Box>
          <FormField label="Prompt">
            <Textarea
              value={editedPromptText}
              onChange={({ detail }) => setEditedPromptText(detail.value)}
              rows={8}
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Delete saved prompt confirmation modal */}
      <Modal
        visible={deletePromptId !== null}
        onDismiss={() => setDeletePromptId(null)}
        header="Delete saved prompt"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeletePromptId(null)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => { deleteSavedPrompt(deletePromptId); setDeletePromptId(null); }}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>Are you sure you want to delete this saved prompt?</Box>
      </Modal>

    </div>
  );
}
